<template>
  <div>
    <warning-banner
        v-if="allWarnings.length"
        :warnings="allWarnings"
        @cta-clicked="(warning) => goToCTA({ warning, source: 'banner'})"
        @multi-warning-cta-clicked="isOverviewModalOpen = true"/>

    <client-only>
      <portal to="MODAL">
        <warnings-overview-modal
            v-if="isOverviewModalOpen"
            :warnings="allWarnings"
            @close="isOverviewModalOpen = false"
            @cta-clicked="(warning) => goToCTA({ warning, source: 'multi-modal'})"
        />
      </portal>
    </client-only>

    <client-only>
      <portal to="MODAL">
        <warning-modal
            v-if="warningsToBeShown.length"
            @close="close(activeWarning)">
          <transition name="WARNING-CONTENT-ANIMATION" mode="out-in">
            <warning-content
                :warning="activeWarning"
                :key="activeWarning.code"
                @cta-clicked="(warning) => goToCTA({ warning, source: 'modal'})"/>
          </transition>
        </warning-modal>
      </portal>
    </client-only>
  </div>
</template>

<script>
import { mapMutations, mapState } from 'vuex';
import { differenceInHours } from 'date-fns';
import { WARNING_EXPIRATION_IN_HOURS } from '@/constants/warnings';

import WarningModal from './WarningModal/WarningModal';
import WarningContent from './WarningModal/WarningContent';
import WarningBanner from './WarningBanner';
import WarningsOverviewModal from './WarningBanner/WarningsOverviewModal';

// Checks if this was read more than 12 hours ago
const isExpired = (warning) => {
  return differenceInHours(
    new Date(),
    new Date(warning.timestamp)
  ) > WARNING_EXPIRATION_IN_HOURS;
};

export default {
  name: 'warnings',
  components: {
    WarningModal,
    WarningContent,
    WarningBanner,
    WarningsOverviewModal,
  },
  data () {
    return {
      isOverviewModalOpen: false,
    };
  },
  computed: {
    ...mapState('warnings', {
      readWarnings: state => Object.values(state.read),
      allWarnings: state => Object.values(state.all),
    }),
    prunedReadWarnings () {
      return this.readWarnings.filter(w => !isExpired(w));
    },
    warningsToBeShown () {
      // return all the warnings that are new or were not read within 12 hours
      return this.allWarnings
        .filter(w => !this.prunedReadWarnings.some(readWarning => readWarning.code === w.code));
    },
    activeWarning () {
      return this.warningsToBeShown[0];
    },
  },
  watch: {
    isOverviewModalOpen (value) {
      this.trackWarning({
        warning: null,
        source: 'multi-modal',
        type: value ? 'open' : 'dismiss',
      });
    },
    activeWarning (value) {
      if (value) {
        this.trackWarning({
          warning: value,
          source: 'modal',
          type: 'open',
        });
      }
    },
  },
  mounted () {
    this.$store.commit('warnings/load');
    this.$store.dispatch('warnings/fetchWarnings');
  },
  methods: {
    ...mapMutations('warnings', ['markAsRead']),
    goToCTA ({ warning, source }) {
      if (warning.extra_info.url) {
        this.trackWarning({
          warning,
          source,
          type: 'cta-click',
        });
        this.markAsRead({ warning });
        // TODO: This shouldn't be a timeout, $events should return a promises
        setTimeout(() => {
          window.location.href = warning.extra_info.url;
        }, 1000);
      }
    },
    close (warning) {
      this.markAsRead({ warning });
      this.trackWarning({
        warning,
        source: 'modal',
        type: 'dismiss',
      });
    },
    trackWarning ({ warning, source, type }) {
      this.$events.$emit(this.$events.PRO_WARNING, {
        reason: warning ? warning.code : null,
        type,
        source,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
  @import "~@homeservices/design-kit/tools";

  .WARNING-CONTENT-ANIMATION-enter-active,
  .WARNING-CONTENT-ANIMATION-leave-active {
    transition: transform $transition-speed-standard ease, opacity $transition-speed-standard ease;
  }
  .WARNING-CONTENT-ANIMATION-leave-to {
    transform: scale3d(0.5, 0.5, 0);
    opacity: 0;
  }
  .WARNING-CONTENT-ANIMATION-enter {
    transform: translate3d(0, 80%, 0);
  }
</style>
